import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import SipSettingForm from 'routes/SipSetting/components/SipSettingForm';
import { Ref } from 'routes/SipSetting/components/SipSettingForm/SipSettingForm';
import { RootState } from 'data/root.reducers';
import * as sipSettingSelectors from 'data/sipSetting/sipSetting.selectors';
import * as sipSettingActions from 'data/sipSetting/sipSetting.actions';
import * as dfniActions from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.actions';
import * as dfniSelectors from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import {
  SipSetting,
  SipSettingSection,
  SipInfo,
  SipTimer,
} from 'data/sipSetting/sipSetting.types';
import { useSipSettingFieldCreateNew } from 'routes/SipSetting/hooks/useSipSettingFieldCreateNew';
import { sipSettingTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptTemplateFieldChanges
} from '../../utils/sipSettingTemplate.adapter';
import {
  getAuditDetailsToSubmit
} from '../../utils/sipSettingDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedSipSetting: sipSettingSelectors
    .getSelectedSipSetting(state)[0],
  originalSelectedSipSetting: sipSettingSelectors
    .getSelectedSipSetting(state)[0],
  sipSetting: sipSettingSelectors.getSipSetting(state),
  sipSettingAdaptedTemplate: adaptTemplateVisibilityMode(sipSettingTemplate, 'edit'),
  deliveryFunctionNetworkInterfaces: dfniSelectors.getDeliveryFunctionNetworkInterfaceList(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  isSipSettingLoading: sipSettingSelectors.isSipSettingLoading(state),
});

const mapDispatch = {
  goToSipSettingView: sipSettingActions.goToSipSettingView,
  getSipSetting: sipSettingActions.getSipSetting,
  updateSipSetting: sipSettingActions.updateSipSetting,
  getDfnis: dfniActions.getDeliveryFunctionNetworkInterfaceList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const SipSettingEdit = ({
  history,
  deliveryFunctionId,
  sipSetting,
  sipSettingAdaptedTemplate,
  deliveryFunctionNetworkInterfaces,
  isAuditEnabled,
  userName,
  userId,
  isSipSettingLoading,
  getSipSetting,
  goToSipSettingView,
  getDfnis,
  updateSipSetting,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SipSetting>>({});
  const [template, setTemplate] = useState<FormTemplate>(sipSettingAdaptedTemplate as FormTemplate);
  const { t } = useTranslation();

  const [handleFieldCreateNew] = useSipSettingFieldCreateNew(
    (field, fieldSection) => handleFieldUpdate(field, fieldSection),
  );

  useEffect(() => {
    getSipSetting(deliveryFunctionId);
    getDfnis(deliveryFunctionId);
  }, [deliveryFunctionId, getSipSetting, getDfnis]);

  useEffect(() => {
    if (sipSettingAdaptedTemplate == null) {
      return;
    }

    if (sipSetting != null) {
      setFormData(sipSetting);
    }

    let clonedTemplate = cloneDeep(sipSettingAdaptedTemplate) as FormTemplate;
    clonedTemplate = adaptTemplateFieldChanges(clonedTemplate, 'encryptPani', sipSetting?.sipInfo?.encryptPani as string);
    clonedTemplate = adaptTemplateOptions(clonedTemplate, deliveryFunctionNetworkInterfaces) as FormTemplate;
    clonedTemplate.formId = Date.now().toString();

    setTemplate(clonedTemplate as FormTemplate);

  }, [sipSetting, sipSettingAdaptedTemplate, deliveryFunctionNetworkInterfaces]);

  const handleCancel = () => goToSipSettingView(history);

  const handleClickSave = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const sipSettingData = formRef.current.getValues(false);

    const payload = {
      data: sipSettingData,
      auditDetails: getAuditDetailsToSubmit(
        sipSettingData,
        sipSetting,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    updateSipSetting(deliveryFunctionId, (sipSetting?.id || sipSetting?.sipInfo?.id) as string, payload, history);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam, filedSection: SipSettingSection) => {

    setTemplate(adaptTemplateFieldChanges(template, fieldName, value));

    const newFormData = cloneDeep(formRef?.current?.getValues(true));

    if (filedSection === SipSettingSection.sipInfo) {
      if (newFormData) {
        newFormData.sipInfo[fieldName as keyof SipInfo] = value;
        setFormData(newFormData);
      }
    } else if (filedSection === SipSettingSection.sipTimer) {
      if (newFormData) {
        newFormData.sipTimer[fieldName as keyof SipTimer] = value;
        setFormData(newFormData);
      }
    }
  };

  const renderForm = () => {
    if (template) {
      return (
        <SipSettingForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      );
    }
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('sipSetting.edit.title')}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (sipSetting == null || isSipSettingLoading) {
    return <div />;
  }

  return (
    <div data-testid="SipSettingEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SipSettingEdit));
